import { useState } from "react";
import type { AcceptBlock as AcceptBlockType } from "@/types";
import { Button, TextAreaField } from "@/components/ui";
import { useRenderer } from "../context";
import { computeTotals, formatMoney } from "@/lib/money";

/**
 * Accept-and-sign widget (CD layout, spec §5.5). A centred panel in the paper-50
 * band: name field + full-width "Accept & sign — {total}". Behaviour forks on
 * mode only (§7.4): public fires onAccept/onDecline; preview renders identically
 * but inert. An empty name focuses the signature input (the accept endpoint
 * requires a name). Decline is kept (per decision) as a quiet control beneath
 * the primary button, opening the optional-reason step.
 */
export function AcceptBlock({ block }: { block: AcceptBlockType }) {
  const { mode, quote, lineItems, selectedOptionalIds, onAccept, onDecline, acceptBusy, acceptError } =
    useRenderer();
  const askReason = block.props.ask_decline_reason ?? true;
  const inert = mode === "preview";

  const currency = quote?.currency ?? "GBP";
  const vatTreatment = quote?.vat_treatment || "inclusive";
  const total = computeTotals(lineItems, vatTreatment, selectedOptionalIds).total;

  const [view, setView] = useState<"idle" | "declining">("idle");
  const [name, setName] = useState("");
  const [reason, setReason] = useState("");
  const [touched, setTouched] = useState(false);

  const nameMissing = name.trim().length === 0;

  function submitAccept() {
    if (inert || !onAccept) return;
    setTouched(true);
    if (nameMissing) {
      document.getElementById("signName")?.focus();
      return;
    }
    onAccept(name.trim());
  }

  function submitDecline() {
    if (inert || !onDecline) return;
    onDecline(reason.trim() || undefined);
  }

  return (
    <section className="block block--accept qr-accept" id="accept">
      <div className="qr-accept__center">
        <p className="eyebrow">Ready to begin</p>
        <h2 className="qr-accept__title">Accept &amp; sign this proposal</h2>

        {acceptError ? <p className="block--accept__error" role="alert">{acceptError}</p> : null}

        {view === "idle" ? (
          <>
            <p className="qr-accept__lede">
              By signing below you accept this proposal at the total shown above. Full terms and
              conditions are at the foot of this page.
            </p>
            <div className="qr-accept__panel">
              <label className="qr-accept__label" htmlFor="signName">
                Type your full name to sign
              </label>
              <input
                id="signName"
                className="qr-accept__input"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Jane Smith"
                autoComplete="name"
                aria-invalid={touched && nameMissing ? true : undefined}
              />
              {touched && nameMissing ? (
                <p className="qr-accept__err" role="alert">
                  Please enter your name to accept.
                </p>
              ) : null}
              <button
                type="button"
                className="qr-accept__submit"
                onClick={submitAccept}
                disabled={acceptBusy}
              >
                {acceptBusy ? "Working…" : <>Accept &amp; sign — {formatMoney(total, currency)}</>}
              </button>
              {askReason ? (
                <button
                  type="button"
                  className="qr-accept__decline"
                  onClick={() => setView("declining")}
                  disabled={acceptBusy}
                >
                  Prefer to decline?
                </button>
              ) : null}
            </div>
          </>
        ) : (
          <div className="qr-accept__panel qr-accept__panel--decline">
            <TextAreaField
              label="Reason for declining (optional)"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Anything you’d like us to know?"
            />
            <div className="qr-accept__actions">
              <Button variant="danger" size="lg" loading={acceptBusy} onClick={submitDecline}>
                Confirm decline
              </Button>
              <Button variant="ghost" size="lg" disabled={acceptBusy} onClick={() => setView("idle")}>
                Back
              </Button>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
